/**
 * Profesyonel dashboard — tam KPI, uyarı kuyrukları, müvekkil ad-soyad, mobil uyum.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    ArrowDownRight,
    ArrowUpRight,
    ChevronRight,
    Clock,
    ExternalLink,
    HelpCircle,
    User,
} from "lucide-react";
import DashboardInsights from "../DashboardInsights";
import DashboardDiagnostics from "../DashboardDiagnostics";
import AkilDashboardWidget from "../ai/AkilDashboardWidget";
import WhatsAppDashboardWidget from "../whatsapp/WhatsAppDashboardWidget";
import ZamanAsimiPanel from "../ZamanAsimiPanel";
import IncomingEmailPanel from "../IncomingEmailPanel";
import SystemActivityPanel from "../SystemActivityPanel";
import {
    DashboardAvukatTable,
    DashboardBasvuruChart,
    DashboardCommandHub,
    DashboardEvrakHeatmap,
    DashboardFinancePanel,
    DashboardMonthCompare,
    DashboardPipeline,
    DashboardSlaPanel,
    DashboardTodayTasks,
    DashboardWeekCalendar,
    DEFAULT_COMMAND_HUB,
} from "./DashboardEliteSections";
import { ALERT_QUEUE_META } from "./dashboardKpiCatalog";
import { MonthInsightLux, ModuleHubLux, LuxPanelHeader, KpiGroupSection } from "./DashboardUltraCards";
import { formatTR, tooltipStyle } from "./theme";

export { KPI_CATALOG } from "./dashboardKpiCatalog";

export const PRO_SECTIONS = [
    {
        id: "komuta",
        label: "Komuta Merkezi",
        keywords: "özet ajanda görev bildirim tahkim yatar ödeme",
        desc: "Acil uyarılar, yapılacaklar ve günlük ajanda",
    },
    {
        id: "operasyon",
        label: "Operasyon",
        keywords: "yatar tahkim sla evrak huni dosya süre",
        desc: "Dosya hunisi, listeler ve zaman aşımı",
    },
    {
        id: "finans",
        label: "Finans",
        keywords: "ödeme tahsilat fatura hakediş masraf",
        desc: "Bekleyen ödemeler, hakediş ve kasa",
    },
    {
        id: "analiz",
        label: "Analiz",
        keywords: "grafik trend dağılım şirket aktivite",
        desc: "Trend grafikleri ve performans",
    },
    {
        id: "iletisim",
        label: "İletişim & AI",
        keywords: "whatsapp akıl email eposta",
        desc: "WhatsApp, Akıl ve e-posta merkezi",
    },
    {
        id: "sistem",
        label: "Sistem",
        keywords: "tanı diagnostik modül kayıt",
        desc: "Kayıt sayıları ve sistem nabzı",
    },
];

const ACCENT = "#D4AF37";

const TONE_CLASS = {
    rose: "border-rose-500/30 bg-rose-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
    orange: "border-orange-500/30 bg-orange-500/5",
    emerald: "border-emerald-500/30 bg-emerald-500/5",
};

export function calcTrend(cur, prev) {
    if (prev == null || prev === 0) return null;
    const pct = ((cur - prev) / prev) * 100;
    return { pct: Math.abs(pct).toFixed(1), up: pct >= 0 };
}

export function pickNum(obj, keys, fb = 0) {
    if (!obj) return fb;
    for (const k of keys) {
        const v = obj[k];
        if (v === null || v === undefined || v === "") continue;
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
    }
    return fb;
}

export function muvekkilAd(item) {
    if (!item) return "—";
    return (
        item.muvekkilAdSoyad ||
        item.muvekkil_ad_soyad ||
        item.adSoyad ||
        item.muvekkil ||
        item.muvekkil_label ||
        item.muvekkil_ad ||
        "—"
    );
}

export function normalizeFileRow(item) {
    if (!item) return null;
    const id = item.id ?? item.dosya_id ?? item.dosyaId;
    const tc = item.tc ?? item.muvekkil_tc ?? item.muvekkilTc ?? "";
    return {
        id,
        dosyaNo: item.dosyaNo || item.dosya_no || (id ? `#${id}` : "—"),
        muvekkilAdSoyad: muvekkilAd(item),
        tc,
        plaka: item.plaka || "",
        gun: item.gun ?? item.kalanGun ?? item.kalan_gun,
        durum: item.dosya_durumu || item.type || item.category || "",
        tarih: item.tarih || "",
        href: id ? `/e/sigorta-dosyalari/${id}` : null,
    };
}

export function normalizeNotifyItem(it) {
    const id = it.id ?? it.dosya_id;
    return {
        id,
        dosyaNo: it.dosya_no || it.dosyaNo || (id ? `#${id}` : "—"),
        muvekkilAdSoyad: muvekkilAd(it),
        plaka: it.plaka || "—",
        tarih: it.tarih || "",
        href: id ? `/e/sigorta-dosyalari/${id}` : null,
    };
}

export function computeHealth(kpis) {
    if (!kpis) return 0;
    let score = 100;
    if (kpis.tahkimeGidecek > 0) score -= Math.min(20, kpis.tahkimeGidecek * 2);
    if (kpis.yatar > 50) score -= 15;
    else if (kpis.yatar > 20) score -= 8;
    if (kpis.odeme > 10) score -= 10;
    if (kpis.tahkim > 20) score -= 8;
    if (kpis.evrak > 30) score -= 5;
    return Math.max(0, Math.min(100, score));
}

export function InfoTip({ text }) {
    const [open, setOpen] = useState(false);
    return (
        <span className="relative inline-flex shrink-0">
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                className="h-5 w-5 rounded-full flex items-center justify-center text-slate-500 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
                aria-label="Bu kart ne işe yarar?"
                title="Açıklama"
            >
                <HelpCircle className="h-3.5 w-3.5" />
            </button>
            {open ? (
                <>
                    <button
                        type="button"
                        className="fixed inset-0 z-40 cursor-default"
                        aria-label="Kapat"
                        onClick={() => setOpen(false)}
                    />
                    <span className="absolute z-50 right-0 top-full mt-1 w-52 sm:w-64 p-2.5 text-[11px] leading-relaxed text-slate-300 bg-[#1e293b] border border-white/15 rounded-lg shadow-2xl">
                        {text}
                    </span>
                </>
            ) : null}
        </span>
    );
}

export function KpiTile({
    label,
    value,
    trend,
    tone = ACCENT,
    sub,
    help,
    href,
    urgent,
    testId,
    onClick,
}) {
    const inner = (
        <>
            <div
                className="absolute top-0 left-0 w-1 h-full rounded-l-xl opacity-90"
                style={{ background: tone }}
            />
            {urgent && Number(value) > 0 ? (
                <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
            ) : null}
            <div className="pl-2.5 pr-1">
                <div className="flex items-start justify-between gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 line-clamp-2 leading-tight">
                        {label}
                    </span>
                    {help ? <InfoTip text={help} /> : null}
                </div>
                <div className="text-xl sm:text-2xl font-semibold text-white tabular-nums mt-1">
                    {typeof value === "number" || typeof value === "string"
                        ? formatTR(value)
                        : value}
                </div>
                {sub ? (
                    <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
                ) : null}
                {trend ? (
                    <div
                        className={`inline-flex items-center gap-0.5 text-[10px] mt-1 ${
                            trend.up ? "text-emerald-400" : "text-rose-400"
                        }`}
                    >
                        {trend.up ? (
                            <ArrowUpRight className="h-3 w-3" />
                        ) : (
                            <ArrowDownRight className="h-3 w-3" />
                        )}
                        %{trend.pct}
                    </div>
                ) : null}
            </div>
        </>
    );

    const cls =
        "rounded-xl border border-white/10 bg-[#0f1419] p-3 sm:p-3.5 relative overflow-hidden hover:border-white/20 transition-all text-left w-full min-h-[88px]";

    if (href) {
        return (
            <Link to={href} data-testid={testId} className={`${cls} block group`}>
                {inner}
                <ExternalLink className="absolute bottom-2 right-2 h-3 w-3 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
        );
    }
    if (onClick) {
        return (
            <button type="button" data-testid={testId} onClick={onClick} className={cls}>
                {inner}
            </button>
        );
    }
    return (
        <div data-testid={testId} className={cls}>
            {inner}
        </div>
    );
}

export function ProPanel({ title, subtitle, help, action, children, className = "" }) {
    return (
        <div className={`rounded-xl border border-white/10 bg-[#111827]/90 overflow-hidden ${className}`}>
            <div className="px-3 sm:px-4 py-3 border-b border-white/10 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-white truncate">{title}</h3>
                        {help ? <InfoTip text={help} /> : null}
                    </div>
                    {subtitle ? (
                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">{subtitle}</p>
                    ) : null}
                </div>
                {action}
            </div>
            <div className="p-3 sm:p-4">{children}</div>
        </div>
    );
}

function FileRowMobile({ row }) {
    const content = (
        <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0">
            <div className="h-9 w-9 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="font-medium text-white text-sm truncate">{row.muvekkilAdSoyad}</div>
                <div className="font-mono text-[11px] text-[#F3E5AB] mt-0.5">{row.dosyaNo}</div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-slate-500">
                    {row.tc ? <span className="font-mono">TC: {row.tc}</span> : null}
                    {row.plaka ? <span>Plaka: {row.plaka}</span> : null}
                    {row.durum ? <span>{row.durum}</span> : null}
                </div>
            </div>
            {row.gun != null ? (
                <span className="text-[10px] font-mono text-amber-300 bg-amber-500/15 px-2 py-1 rounded shrink-0">
                    {row.gun}g
                </span>
            ) : (
                <ChevronRight className="h-4 w-4 text-slate-600 shrink-0 mt-1" />
            )}
        </div>
    );
    if (row.href) {
        return (
            <Link to={row.href} className="block hover:bg-white/[0.02] -mx-2 px-2 rounded-lg transition-colors">
                {content}
            </Link>
        );
    }
    return content;
}

export function FileTable({ rows, empty = "Kayıt yok." }) {
    const normalized = (rows || []).map(normalizeFileRow).filter(Boolean);
    if (!normalized.length) {
        return <p className="text-sm text-slate-500 py-8 text-center">{empty}</p>;
    }
    return (
        <>
            <div className="md:hidden">{normalized.map((row) => (
                <FileRowMobile key={row.id || row.dosyaNo} row={row} />
            ))}</div>
            <div className="hidden md:block overflow-x-auto -mx-1">
                <table className="w-full text-sm min-w-[480px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/10">
                            <th className="pb-2 pl-1">Müvekkil Ad Soyad</th>
                            <th className="pb-2">TC / VKN</th>
                            <th className="pb-2">Dosya No</th>
                            <th className="pb-2">Plaka</th>
                            <th className="pb-2 text-right pr-1">Süre</th>
                        </tr>
                    </thead>
                    <tbody>
                        {normalized.map((row) => (
                            <tr key={row.id || row.dosyaNo} className="border-b border-white/5 hover:bg-white/[0.03]">
                                <td className="py-2.5 pl-1 font-medium text-white max-w-[180px] truncate">
                                    {row.muvekkilAdSoyad}
                                </td>
                                <td className="py-2.5 font-mono text-xs text-slate-400">
                                    {row.tc || "—"}
                                </td>
                                <td className="py-2.5">
                                    {row.href ? (
                                        <Link to={row.href} className="font-mono text-xs text-[#F3E5AB] hover:underline">
                                            {row.dosyaNo}
                                        </Link>
                                    ) : (
                                        <span className="font-mono text-xs text-slate-400">{row.dosyaNo}</span>
                                    )}
                                </td>
                                <td className="py-2.5 text-slate-400 text-xs">{row.plaka || "—"}</td>
                                <td className="py-2.5 text-right pr-1">
                                    {row.gun != null ? (
                                        <span className="text-[10px] font-mono text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">
                                            {row.gun}g
                                        </span>
                                    ) : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export function AlertQueueGrid({ groups }) {
    if (!groups?.length) {
        return (
            <p className="text-sm text-slate-500 py-6 text-center col-span-full">
                Acil uyarı kuyruğu boş — operasyon akışı normal.
            </p>
        );
    }
    return groups.map((g) => {
        const meta = ALERT_QUEUE_META[g.key] || {
            title: g.label,
            desc: "İlgili dosyalar",
            action: "Dosyayı inceleyin",
            tone: "amber",
        };
        const items = (g.items || []).map(normalizeNotifyItem);
        const toneCls = TONE_CLASS[meta.tone] || TONE_CLASS.amber;
        return (
            <ProPanel
                key={g.key}
                title={meta.title}
                subtitle={`${formatTR(g.count)} kayıt · ${meta.desc}`}
                help={`Ne yapmalı: ${meta.action}`}
                action={
                    g.count > 0 ? (
                        <Link to="/e/sigorta-dosyalari" className="text-[10px] text-[#D4AF37] hover:underline shrink-0">
                            Tümü
                        </Link>
                    ) : null
                }
                className={`${toneCls}`}
            >
                {!items.length ? (
                    <p className="text-xs text-slate-500 text-center py-4">Liste boş</p>
                ) : (
                    <ul className="divide-y divide-white/5 max-h-[220px] overflow-y-auto">
                        {items.slice(0, 12).map((it, idx) => (
                            <li key={it.id || idx}>
                                {it.href ? (
                                    <Link
                                        to={it.href}
                                        className="flex items-center gap-2 py-2.5 hover:bg-white/[0.03] -mx-1 px-1 rounded-md group"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-white font-medium truncate group-hover:text-[#F3E5AB]">
                                                {it.muvekkilAdSoyad}
                                            </div>
                                            <div className="text-[11px] text-slate-500 font-mono truncate">
                                                {it.dosyaNo}
                                                {it.plaka && it.plaka !== "—" ? ` · ${it.plaka}` : ""}
                                            </div>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-slate-600 shrink-0" />
                                    </Link>
                                ) : (
                                    <div className="py-2 text-sm">
                                        <div className="text-white font-medium">{it.muvekkilAdSoyad}</div>
                                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">{it.dosyaNo}</div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </ProPanel>
        );
    });
}

export function HealthGauge({ score, yatar, tahkim, tahkimeGidecek }) {
    const label =
        score >= 80 ? "İyi" : score >= 60 ? "Orta" : score >= 40 ? "Dikkat" : "Kritik";
    const color =
        score >= 80 ? "#34D399" : score >= 60 ? "#F59E0B" : score >= 40 ? "#FB7185" : "#EF4444";
    return (
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
            <div className="relative h-20 w-20 sm:h-24 sm:w-24 shrink-0">
                <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${score} 100`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl sm:text-2xl font-bold text-white tabular-nums">{score}</span>
                </div>
            </div>
            <div className="text-center sm:text-left">
                <div className="text-base sm:text-lg font-semibold text-white">Operasyon Sağlığı</div>
                <div className="text-sm font-medium" style={{ color }}>{label}</div>
                <div className="text-xs text-slate-500 mt-2 space-y-1">
                    <div>Tahkime geçmesi gereken: <span className="text-rose-400 tabular-nums">{formatTR(tahkimeGidecek)}</span></div>
                    <div>Yatar: {formatTR(yatar)} · Tahkim: {formatTR(tahkim)}</div>
                </div>
            </div>
        </div>
    );
}

export function AgendaList({ items }) {
    if (!items?.length) {
        return <p className="text-sm text-slate-500 py-4 text-center">Bugün randevu yok.</p>;
    }
    return (
        <ul className="divide-y divide-white/5">
            {items.map((a, i) => (
                <li key={i} className="py-2.5">
                    <div className="flex gap-3 text-sm">
                        <span className="font-mono text-[#D4AF37] shrink-0 w-11">{a.saat}</span>
                        <div className="min-w-0">
                            <div className="text-slate-200 truncate">{a.baslik}</div>
                            {a.muvekkil ? (
                                <div className="text-xs text-slate-500 truncate mt-0.5">{a.muvekkil}</div>
                            ) : null}
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}

export function ActivityList({ items }) {
    if (!items?.length) {
        return <p className="text-sm text-slate-500 py-4 text-center">Aktivite yok.</p>;
    }
    return (
        <ul className="divide-y divide-white/5 max-h-[320px] overflow-y-auto">
            {items.map((item, i) => (
                <li key={i} className="py-2.5 text-sm">
                    <div className="text-slate-200">{item.baslik || item.title}</div>
                    {item.detay ? <div className="text-xs text-slate-500 truncate">{item.detay}</div> : null}
                    {item.tarih ? (
                        <div className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(item.tarih).toLocaleString("tr-TR")}
                        </div>
                    ) : null}
                </li>
            ))}
        </ul>
    );
}

export function TrendChart({ data }) {
    if (!data?.length) {
        return <p className="text-sm text-slate-500 py-8 text-center">Trend verisi yok.</p>;
    }
    return (
        <div className="h-56 sm:h-64">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} width={32} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area type="monotone" dataKey="dosya" name="Dosya" stroke={ACCENT} fill={`${ACCENT}33`} strokeWidth={2} />
                    <Area type="monotone" dataKey="muvekkil" name="Müvekkil" stroke="#60A5FA" fill="#60A5FA33" strokeWidth={2} />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

export function OdemeList({ list }) {
    if (!list?.length) {
        return <p className="text-sm text-slate-500 py-4 text-center">Bekleyen ödeme yok.</p>;
    }
    return (
        <ul className="divide-y divide-white/5 max-h-[320px] overflow-y-auto">
            {list.map((row) => {
                const ad = row.muvekkilAdSoyad || row.muvekkil_ad_soyad || "—";
                const href = row.dosyaId ? `/e/sigorta-dosyalari/${row.dosyaId}` : null;
                const inner = (
                    <div className="flex items-center justify-between gap-3 py-3">
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-white truncate">{ad}</div>
                            <div className="font-mono text-[11px] text-slate-500 mt-0.5">
                                Dosya: {row.dosyaNo || "—"}
                            </div>
                        </div>
                        <span className="text-sm text-white tabular-nums shrink-0 font-semibold">
                            {Number(row.odemeMiktari || 0).toLocaleString("tr-TR")} ₺
                        </span>
                    </div>
                );
                return (
                    <li key={row.id}>
                        {href ? (
                            <Link to={href} className="block hover:bg-white/[0.02] -mx-2 px-2 rounded-lg">{inner}</Link>
                        ) : inner}
                    </li>
                );
            })}
        </ul>
    );
}

export function useDashboardModel(data) {
    return useMemo(() => {
        const prev = data?.previous || {};
        const kpis = {
            muvekkil: pickNum(data, ["muvekkil", "toplam_muvekkil"]),
            dosya: pickNum(data, ["dosya", "toplam_dosya"]),
            degerKaybi: pickNum(data, ["degerKaybi"]),
            araci: pickNum(data, ["araci"]),
            tahkim: pickNum(data, ["tahkim_bekleyen", "tahkimSurecSayisi"]),
            yatar: pickNum(data, ["yatarAcilacakSayisi"], pickNum(data?.yatar_dosyalar, ["yatar"])),
            tahkimeGidecek: pickNum(data, ["bekleyenBasvuru16Gun"]),
            evrak: pickNum(data, ["bekleyen_evrak"]),
            odeme: pickNum(data, ["bekleyen_odeme", "odeme_bekleyen"]),
            fatura: pickNum(data, ["fatura_bekleyen", "bekleyen_fatura"]),
            randevu: pickNum(data, ["randevu_bugun"]),
            hatirlatma: pickNum(data, ["hatirlatma_bugun", "aktif_hatirlatma"]),
            whatsapp: pickNum(data, ["whatsapp_bekleyen", "whatsappBekleyen"]),
            icra: pickNum(data, ["aktif_icralar"]),
            yeniMuvekkil: pickNum(data, ["yeni_muvekkil_30"]),
            yeniDosya: pickNum(data, ["yeni_dosya_30"]),
            buAyMuvekkil: pickNum(data, ["buAyMuvekkil"]),
            buAyDosya: pickNum(data, ["buAySigortaDosya"]),
            tahsilatDonem: pickNum(data?.odemeOzet, ["odemeMiktariToplam"], pickNum(data?.tahsilat_toplam, ["tahsilat"])),
        };
        const trends = {
            muvekkil: calcTrend(kpis.muvekkil, pickNum(prev, ["muvekkil", "toplam_muvekkil"])),
            dosya: calcTrend(kpis.dosya, pickNum(prev, ["dosya", "toplam_dosya"])),
            tahkim: calcTrend(kpis.tahkim, pickNum(prev, ["tahkim_bekleyen"])),
            yatar: calcTrend(kpis.yatar, pickNum(prev?.yatar_dosyalar, ["yatar"])),
            yeniMuvekkil: calcTrend(kpis.yeniMuvekkil, pickNum(prev, ["yeni_muvekkil_30"])),
            yeniDosya: calcTrend(kpis.yeniDosya, pickNum(prev, ["yeni_dosya_30"])),
        };
        const alertGroups = (data?.advanced_notifications || []).filter((g) => (g.count ?? 0) > 0);
        const mapRows = (arr) => (arr || []).slice(0, 20);
        return {
            kpis,
            trends,
            health: computeHealth(kpis),
            chartData: (data?.chartData || []).map((d) => ({
                name: d?.name ?? "",
                dosya: Number(d?.dosya ?? 0) || 0,
                muvekkil: Number(d?.muvekkil ?? 0) || 0,
            })),
            agenda: (data?.bugunAjanda || []).slice(0, 8),
            activity: (data?.aktivite || []).slice(0, 15),
            yatarRows: mapRows(data?.yatarDosyalari),
            tahkimRows: mapRows(data?.tahkimDosyalari),
            kritikRows: mapRows(data?.kritikDosyalar),
            tahkimeGidecekRows: mapRows(data?.tahkimeGidecekSatirlari),
            gecikmisTahkimRows: mapRows(data?.gecikmisTahkimSatirlari),
            odemeList: data?.bekleyen_odeme_listesi || data?.bekleyenOdemeListesi || [],
            alertGroups,
        };
    }, [data]);
}

export function SectionKomuta({ data, loading, model, kpiGroups, periodLabel, onKpiClick }) {
    return (
        <div className="space-y-6">
            <LuxPanelHeader
                title="Komuta Merkezi"
                subtitle="Aylık özet, tüm KPI kartları, acil kuyruklar ve günlük işler"
                help="Her KPI kartının altında ne anlama geldiği ve hangi modüle gittiği yazar. Kırmızı ACİL etiketi öncelikli işlem gerektirir."
            />

            <MonthInsightLux
                monthCmp={data?.month_comparison}
                weekDays={data?.week_calendar}
                chartData={model.chartData}
            />

            {kpiGroups?.map((g) => (
                <KpiGroupSection
                    key={g.id}
                    group={g}
                    periodLabel={periodLabel}
                    kpis={model.kpis}
                    trends={model.trends}
                    onKpiClick={onKpiClick}
                />
            ))}

            <div className="mb-1">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    Operasyon uyarı kuyrukları
                    <InfoTip text="Tahkime geçmesi gereken, yatar, yeni dosya ve yaklaşan tahkim kuyrukları. Müvekkil ad soyad ile listelenir; tıklayarak dosyaya gidin." />
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                    Tahkime geçmesi gereken · Yatar · Yeni dosya — müvekkil ad soyad görünür
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <AlertQueueGrid groups={model.alertGroups} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <ProPanel
                    title="Operasyon sağlığı"
                    help="Yatar, tahkim ve tahkime geçmesi gereken dosya yüküne göre 0–100 skor."
                >
                    <HealthGauge
                        score={model.health}
                        yatar={model.kpis.yatar}
                        tahkim={model.kpis.tahkim}
                        tahkimeGidecek={model.kpis.tahkimeGidecek}
                    />
                </ProPanel>
                <ProPanel title="Bugünün ajandası" subtitle={`${model.kpis.randevu} randevu`} help="Planlanmış randevular — saat ve müvekkil adı.">
                    <AgendaList items={model.agenda} />
                </ProPanel>
                <ProPanel title="Bekleyen ödemeler (özet)" subtitle={`${formatTR(model.kpis.odeme)} kayıt`} help="Ödeme yapılmamış dosyalar — müvekkil ad soyad ve tutar.">
                    <OdemeList list={model.odemeList.slice(0, 5)} />
                    {model.kpis.odeme > 5 ? (
                        <Link to="/e/odemeler" className="block text-center text-xs text-[#D4AF37] mt-3 hover:underline">
                            +{model.kpis.odeme - 5} kayıt daha → Ödemeler
                        </Link>
                    ) : null}
                </ProPanel>
            </div>

            <DashboardTodayTasks tasks={data?.today_tasks} loading={loading} />
            <DashboardWeekCalendar days={data?.week_calendar} />
        </div>
    );
}

const FILE_TABS = [
    { id: "tahkime_gidecek", label: "Tahkime geçmeli", key: "tahkimeGidecekRows", countKey: "tahkimeGidecek" },
    { id: "yatar", label: "Yatar", key: "yatarRows", countKey: "yatar" },
    { id: "tahkim", label: "Tahkim", key: "tahkimRows", countKey: "tahkim" },
    { id: "gecikmis", label: "Gecikmiş tahkim", key: "gecikmisTahkimRows", countKey: null },
    { id: "kritik", label: "Kritik", key: "kritikRows", countKey: null },
];

export function SectionOperasyon({ data, fileTab, setFileTab, model }) {
    const activeDef = FILE_TABS.find((t) => t.id === fileTab) || FILE_TABS[0];
    const rows = model[activeDef.key] || [];
    const count = activeDef.countKey ? model.kpis[activeDef.countKey] : rows.length;

    return (
        <div className="space-y-5">
            <LuxPanelHeader
                title="Operasyon"
                subtitle="Dosya hunisi, SLA, evrak ısı haritası ve müvekkil ad-soyad listeleri"
                help="Sekmeler: tahkime geçmesi gereken, yatar, tahkim, gecikmiş tahkim ve kritik dosyalar. Her satır ilgili dosyaya gider."
            />
            <DashboardPipeline pipeline={data?.pipeline} />
            <ProPanel
                title="Dosya listeleri — müvekkil ad soyad"
                subtitle="Sekmeler arasında geçin; her satır dosyaya link verir"
                help="Tahkime geçmesi gereken: 16 günü dolmuş başvurular. Yatar: yatar konusu dosyalar. Tahkim: tahkim sürecindekiler."
                action={
                    <Link to="/yeni/zaman-asimi" className="text-xs text-[#D4AF37] hover:underline">
                        Zaman aşımı →
                    </Link>
                }
            >
                <div className="flex gap-1 mb-4 overflow-x-auto pb-1 snap-x snap-mandatory">
                    {FILE_TABS.map((t) => {
                        const c = t.countKey ? model.kpis[t.countKey] : (model[t.key]?.length || 0);
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setFileTab(t.id)}
                                className={`shrink-0 snap-start px-3 py-2 text-xs rounded-lg border transition-colors whitespace-nowrap ${
                                    fileTab === t.id
                                        ? "bg-[#D4AF37]/15 text-[#F3E5AB] border-[#D4AF37]/35"
                                        : "text-slate-500 border-white/10 hover:text-slate-300"
                                }`}
                            >
                                {t.label} ({formatTR(c)})
                            </button>
                        );
                    })}
                </div>
                <FileTable rows={rows} empty={`${activeDef.label} listesi boş.`} />
            </ProPanel>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <DashboardSlaPanel alerts={data?.sla_alerts} />
                <ProPanel title="Zaman aşımı takibi" help="Kırmızı / turuncu / sarı süre dilimlerindeki dosyalar.">
                    <ZamanAsimiPanel compact />
                </ProPanel>
            </div>
            <DashboardEvrakHeatmap stats={data?.eksik_evrak_stats} />
            <DashboardAvukatTable rows={data?.avukat_performance} />
        </div>
    );
}

export function SectionFinans({ data, loading, model }) {
    return (
        <div className="space-y-5">
            <LuxPanelHeader
                title="Finans & Tahsilat"
                subtitle="Bekleyen ödemeler, dönem özeti ve ay karşılaştırması"
                help="Ödeme listesinde müvekkil ad soyad ve tutar görünür. Dönem özeti seçili periyottaki tahsilat ve hakediş toplamlarını gösterir."
            />
            <DashboardFinancePanel finance={data?.finance_summary} loading={loading} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <ProPanel
                    title="Bekleyen ödemeler"
                    subtitle={`${formatTR(model.kpis.odeme)} kayıt — müvekkil ad soyad ve tutar`}
                    help="Ödeme yapılmamış kayıtlar. Müvekkil adına tıklayarak ilgili dosyaya gidebilirsiniz."
                    action={<Link to="/e/odemeler" className="text-xs text-[#D4AF37] hover:underline">Tüm ödemeler</Link>}
                >
                    <OdemeList list={model.odemeList} />
                </ProPanel>
                <ProPanel title="Dönem özeti" help="Seçili dönemdeki tahsilat, MBS ve müvekkil hakediş toplamları.">
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            ["Kayıt sayısı", data?.odemeOzet?.kayitSayisi, false],
                            ["Brüt tahsilat", data?.odemeOzet?.odemeMiktariToplam, true],
                            ["MBS hakediş", data?.odemeOzet?.mbsHakedisToplam, true],
                            ["Müvekkil net", data?.odemeOzet?.sigortaliHakedisToplam, true],
                            ["Toplam masraf", data?.odemeOzet?.toplamMasraf, true],
                            ["Ödendi (dönem)", data?.odemeOzet?.odemeYapildiSayisi, false],
                        ].map(([label, val, isMoney]) => (
                            <div key={label} className="rounded-lg border border-white/10 p-3 bg-white/[0.02]">
                                <div className="text-[10px] text-slate-500 uppercase leading-tight">{label}</div>
                                <div className="text-lg text-white tabular-nums mt-1">
                                    {val != null
                                        ? Number(val).toLocaleString("tr-TR", { maximumFractionDigits: 0 })
                                        : "—"}
                                    {isMoney ? " ₺" : ""}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-3 text-xs">
                        <Link to="/yeni/odeme-raporlari" className="text-[#D4AF37] hover:underline">Ödeme raporları →</Link>
                        <Link to="/yeni/kasa-hesaplar" className="text-slate-400 hover:text-white">Kasa hesapları →</Link>
                    </div>
                </ProPanel>
            </div>
            <DashboardMonthCompare cmp={data?.month_comparison} />
        </div>
    );
}

export function SectionAnaliz({ data, model }) {
    return (
        <div className="space-y-5">
            <LuxPanelHeader
                title="Analiz & Trend"
                subtitle="12 aylık akış, başvuru dağılımı ve sistem aktivitesi"
                help="Grafikler seçili döneme göre güncellenir. Başvuru konusu dağılımı dosya türlerinin yoğunluğunu gösterir."
            />
            <ProPanel title="Aylık akış" subtitle="Dosya ve müvekkil trendi (12 ay)" help="Aylık yeni dosya ve müvekkil hareketini gösterir.">
                <TrendChart data={model.chartData} />
            </ProPanel>
            <DashboardInsights data={data} variant="full" />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <DashboardBasvuruChart data={data?.basvuruKonusuDagilim} />
                <ProPanel title="Son sistem hareketleri" help="Yeni müvekkil, dosya ve evrak kayıtları.">
                    <ActivityList items={model.activity} />
                </ProPanel>
            </div>
        </div>
    );
}

export function SectionIletisim() {
    return (
        <div className="space-y-5">
            <LuxPanelHeader
                title="İletişim & AI"
                subtitle="WhatsApp, Akıl asistanı ve gelen e-posta eşleme"
                help="WhatsApp bekleyen mesajlar, AI özetleri ve sigorta şirketi e-postalarını dosya numarasıyla eşleme burada yönetilir."
            />
            <AkilDashboardWidget />
            <WhatsAppDashboardWidget />
            <ProPanel title="Gelen e-posta eşleme" help="Sigorta şirketi e-postasını yapıştırın; sistem dosya numarasını bulup eşler.">
                <IncomingEmailPanel />
            </ProPanel>
        </div>
    );
}

export function SectionSistem({ data, loading }) {
    return (
        <div className="space-y-6">
            <LuxPanelHeader
                title="Sistem & Modüller"
                subtitle="Tüm panel modülleri, kayıt sayıları ve sistem tanı"
                help="Modül kartlarına tıklayarak ilgili sayfaya gidin. Diagnostics alanı canlı veritabanı özetini gösterir."
            />
            <ModuleHubLux diagnostics={data?.diagnostics} />
            <DashboardDiagnostics data={data} loading={loading} />
            <ProPanel title="Sistem nabzı" help="Son evrak, mail ve mesaj işlemleri.">
                <SystemActivityPanel />
            </ProPanel>
            <DashboardCommandHub items={Array.isArray(data?.command_hub) ? data.command_hub : data?.command_hub?.links} />
        </div>
    );
}
